<template>
  <section class="container-page">
    <h2 class="title-inner">Импортный потенциал товаров</h2>
    <div class="mb-16">
      <ButtonComponent
        v-permission="permissions.import"
        mod="gradient-bg"
        :disabled="importInProgress"
        @click="importExcel"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
      <ButtonComponent
        v-permission="permissions.export"
        mod="gradient-bg"
        class="ml-2"
        :disabled="exportInProgress"
        @click="exportExcel"
      >
        Сохранить в Excel
      </ButtonComponent>
    </div>
    <div class="mb-4">
      <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
    </div>
    <template v-if="isAccessAllowed">
      <section class="mb-4 d-flex align-items-end gap-2">
        <FilterInput
          label="Период импорта России"
          v-model="filter.importYear"
          type="select2"
          :select2Options="yearOptions"
          :select2Settings="{ ...select2Settings, allowClear: false }"
          v-on:valueChangedDebounced="changeFilter"
        />
        <FilterInput
          label="Период экспорта страны-партнера"
          v-model="filter.exportYear"
          type="select2"
          :select2Options="yearOptions"
          :select2Settings="{ ...select2Settings, allowClear: false }"
          v-on:valueChangedDebounced="changeFilter"
        />
      </section>
      <section>
        <div class="table-container mb-16 js-table-arrow">
          <table class="stockTable">
            <thead>
              <tr class="text-center">
                <th>Код ТН ВЭД ЕАЭС</th>
                <th>Наименование товара</th>
                <th>Импортный потенциал, млн долл.</th>
                <th>Фактический импорт России из страны, млн долл.</th>
                <th>Фактический импорт России из всех стран, млн долл.</th>
                <th>Фактический экспорт страны в мир, млн долл.</th>
                <th>Страна-экспортер</th>
                <th></th>
              </tr>
              <tr>
                <th class="filter">
                  <FilterInput
                    placeholder="Код ТН ВЭД"
                    v-model="filter.tnvedCode"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    placeholder="Наименование товара"
                    v-model="filter.tnvedName"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    placeholder="От"
                    v-model="filter.potentialFrom"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                  <FilterInput
                    placeholder="До"
                    v-model="filter.potentialTo"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    placeholder="От"
                    v-model="filter.importToCountryFrom"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                  <FilterInput
                    placeholder="До"
                    v-model="filter.importToCountryTo"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    placeholder="От"
                    v-model="filter.importWorldFrom"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                  <FilterInput
                    placeholder="До"
                    v-model="filter.importWorldTo"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    placeholder="От"
                    v-model="filter.exportFrom"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                  <FilterInput
                    placeholder="До"
                    v-model="filter.exportTo"
                    type="number"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter">
                  <FilterInput
                    :select2Options="countryList"
                    placeholder="Выберите"
                    type="select2"
                    v-model="filter.toCountryId"
                    :select2Settings="select2Settings"
                    v-on:valueChangedDebounced="changeFilter"
                  />
                </th>
                <th class="filter"></th>
              </tr>
            </thead>
            <tbody v-if="page.items?.length && !isLoading">
              <tr v-for="item in page.items" :key="item.potentialId" class="text-center">
                <td>{{ item.tnvedCode }}</td>
                <td class="text-left">{{ item.tnvedName }}</td>
                <td>{{ item.potentialValue ? formatAdaptiveNumber(item.potentialValue) : '-' }}</td>
                <td>{{ item.importFactFromCountry ? formatAdaptiveNumber(item.importFactFromCountry) : '-' }}</td>
                <td>{{ item.importFactWorld ? formatAdaptiveNumber(item.importFactWorld) : '-' }}</td>
                <td>{{ item.exportFactCountry ? formatAdaptiveNumber(item.exportFactCountry) : '-' }}</td>
                <td class="text-left">{{ item.countryName }}</td>
                <td>
                  <IconComponent v-permission="permissions.edit" @click="editCard(item)" name="mode" />
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="isLoading">
              <tr>
                <td class="text-center" colspan="5">Загрузка...</td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td class="text-center" colspan="5">Нет данных.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="page.pagesTotal > 1">
          <Pagination
            :pageCount="page.pagesTotal"
            :currentPage="page.pageNumber"
            @change="onPageChange($event, false)"
            @more="onPageChange($event, true)"
          />
        </div>
      </section>
    </template>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </section>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import FilterInput from '@/common/components/FilterInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import text from '@/common/mixins/text';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';
  import { getPreviousYear, getYearOptions } from '@/common/utils/year';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import IconComponent from '../../../common/ui/IconComponent.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import Api from '../api/index';

  const PERMISSIONS = {
    view: Constants.Permissions.ImportPotentialView,
    import: Constants.Permissions.ImportPotentialImport,
    export: Constants.Permissions.ImportPotentialExport,
    edit: Constants.Permissions.ImportPotentialEdit,
  };

  export default {
    name: 'ImportPotential',
    components: { ImportModal, ModalComponent, ButtonComponent, FilterInput, DefaultFilter, IconComponent },
    mixins: [security, text],
    data() {
      return {
        route: 'importpotential',
        page: {},
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        importInProgress: false,
        exportInProgress: false,
        importModal: false,
        isLoading: false,
        select2Settings: Constants.select2Settings,
        yearOptions: getYearOptions(2022),
        countryList: [],
        filter: {
          exportYear: getPreviousYear(),
          importYear: getPreviousYear(),
          tnvedCode: '',
          tnvedName: '',
          potentialFrom: null,
          potentialTo: null,
          importToCountryFrom: null,
          importToCountryTo: null,
          importWorldFrom: null,
          importWorldTo: null,
          exportFrom: null,
          exportTo: null,
          toCountryId: null,
        },
      };
    },
    computed: {
      permissions: () => PERMISSIONS,
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    methods: {
      loadDictionaries() {
        Utils.loadSelectOptions('countryList', this.countryList, true);
      },
      loadPage(append) {
        this.isLoading = !append;
        const params = { ...this.filter, ...this.request };
        Api.search(this.route, params)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },

      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        this.closeModal();
        this.importInProgress = true;
        Api.downloadFile(this.route, file)
          .then(() => {
            Constants.alert.fire('Импорт', 'Процесс импорта запущен. Ожидайте уведомление о результате.', 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire('Импорт', 'Ошибка при импорте данных.', 'error');
            this.importInProgress = false;
          });
      },
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      closeModal() {
        this.importModal = false;
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      exportExcel() {
        this.exportInProgress = true;
        Api.exportImportPotentialExcel(this.filter).then(() => {
          this.exportInProgress = false;
        });
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
      editCard(item) {
        this.$router.push({
          name: 'ImportPotentialCard',
          params: { id: item.potentialId, action: 'edit' },
          query: {
            ...(item.exportId != null && { exportId: item.exportId }),
            ...(item.importId != null && { importId: item.importId }),
          },
        });
      },
    },
  };
</script>
